type Identity = { gameNo: number; completedAt: number };
type Request = Identity & {
  attempts: number;
  requestId?: string;
  timer?: ReturnType<typeof setTimeout>;
};

/** Three bounded attempts; reconnect/reopening can resume exhausted requests. */
export const createOwnHandCardsRecovery = (
  prefix: string,
  send: (request: Identity & { requestId: string }) => void,
  onCards: (gameNo: number, cards: string[]) => void
) => {
  const requests = new Map<number, Request>();
  let sequence = 0;
  const attempt = (request: Request) => {
    if (request.attempts >= 3) return;
    request.attempts++;
    request.requestId = `${prefix}-${request.gameNo}-${++sequence}`;
    request.timer = setTimeout(() => retry(request), 8000);
    send({
      gameNo: request.gameNo,
      completedAt: request.completedAt,
      requestId: request.requestId
    });
  };
  const retry = (request: Request) => {
    clearTimeout(request.timer);
    request.requestId = undefined;
    if (request.attempts < 3) request.timer = setTimeout(() => attempt(request), 2000);
  };
  return {
    request(gameNo: number, completedAt: number) {
      if (!Number.isSafeInteger(gameNo) || gameNo <= 0 || !completedAt) return;
      const existing = requests.get(gameNo);
      if (existing && existing.attempts < 3) return;
      if (existing?.requestId) return;
      const request = { gameNo, completedAt, attempts: 0 };
      requests.set(gameNo, request);
      attempt(request);
    },
    receive(payload: any) {
      const request = requests.get(Number(payload?.gameNo));
      if (!request || !request.requestId || request.requestId !== payload?.requestId) return;
      clearTimeout(request.timer);
      if (!payload.success || !Array.isArray(payload.holeCards) || !payload.holeCards.length) {
        retry(request);
        return;
      }
      requests.delete(request.gameNo);
      onCards(request.gameNo, payload.holeCards);
    },
    reconnect() {
      requests.forEach((request) => {
        clearTimeout(request.timer);
        request.attempts = 0;
        attempt(request);
      });
    },
    dispose() {
      requests.forEach((request) => clearTimeout(request.timer));
      requests.clear();
    }
  };
};
